import { ChatComposeBoxAddAttachmentMenuItem } from '@glade-ai/chat-plugin';
import { type ComponentPropsWithoutRef, forwardRef, useCallback } from 'react';

import { mixpanelTrack } from '@/providers/Mixpanel';
import { type UploadedRecording } from '@/providers/ScreenRecord';

const ATTACH_RECORDING_ICON = 'ph--monitor-arrow-up--regular';

type AttachUploadedRecordingMenuItemProps = Omit<
  ComponentPropsWithoutRef<typeof ChatComposeBoxAddAttachmentMenuItem>,
  'icon' | 'label'
> & {
  recording: UploadedRecording;
  onAttachRecording: (mediaId: string, name: string) => void;
};

/** Attachment menu item for attaching a previously uploaded screen recording to a message. */
export const AttachUploadedRecordingMenuItem = forwardRef<HTMLDivElement, AttachUploadedRecordingMenuItemProps>(
  ({ recording, onAttachRecording, ...props }, forwardedRef) => {
    const handleClick = useCallback(
      (event: React.MouseEvent<HTMLDivElement>) => {
        mixpanelTrack('screen_recording_attached_from_upload', {
          mediaId: recording.mediaId,
          durationSeconds: recording.durationSeconds,
        });
        onAttachRecording(recording.mediaId, recording.name);
        props.onClick?.(event);
      },
      [recording, onAttachRecording, props],
    );

    const minutes = Math.floor(recording.durationSeconds / 60);
    const seconds = recording.durationSeconds % 60;
    const durationLabel = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

    return (
      <ChatComposeBoxAddAttachmentMenuItem
        {...props}
        ref={forwardedRef}
        icon={ATTACH_RECORDING_ICON}
        label={`Screen Recording (${durationLabel})`}
        onClick={handleClick}
      />
    );
  },
);
AttachUploadedRecordingMenuItem.displayName = 'AttachUploadedRecordingMenuItem';
